import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase/config';
import {
  collection, addDoc, getDocs, doc, getDoc, updateDoc, setDoc,
  query, orderBy, writeBatch
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  Box, Container, Typography, Button, Paper, TextField,
  IconButton, Alert, CircularProgress, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, LinearProgress,
  Collapse, List, ListItem, ListItemText, Tooltip, InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';

const DEFAULT_CATEGORIES = ['Bevis', 'Certifieringar', 'Försäkringar', 'Marknad', 'Miljö', 'Mål', 'Policy', 'Ramavtal', 'Övrigt'];
const ALLOWED_ROLES = ['superadmin', 'admin', 'gruppledare', 'anbudsansvarig'];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

function ExpiryChip({ expiresAt }) {
  const days = daysUntil(expiresAt);
  if (days === null) return null;
  if (days < 0) return (
    <Chip label="Utgången" size="small" color="error" icon={<WarningAmberIcon />} />
  );
  if (days <= 30) return (
    <Chip label={`Går ut om ${days} dagar`} size="small" color="warning"
      icon={<WarningAmberIcon />} />
  );
  return (
    <Chip label={`Giltig t.o.m. ${expiresAt}`} size="small" color="success" variant="outlined" />
  );
}

export default function DocumentLibrary() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState(''); // eslint-disable-line no-unused-vars
  const [uploadProgress, setUploadProgress] = useState({});
  const [expandedHistory, setExpandedHistory] = useState({});

  // New document dialog
  const [newDocDialog, setNewDocDialog] = useState(false);
  const [newDocForm, setNewDocForm] = useState({
    name: '', category: 'Certifieringar', expiresAt: '', note: ''
  });
  const [newDocFile, setNewDocFile] = useState(null);

  // New version dialog
  const [newVersionDialog, setNewVersionDialog] = useState(null); // docId
  const [newVersionFile, setNewVersionFile] = useState(null);
  const [newVersionNote, setNewVersionNote] = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Edit metadata
  const [editMetaDoc, setEditMetaDoc] = useState(null); // doc object
  const [editMetaForm, setEditMetaForm] = useState({ name: '', category: '', expiresAt: '', note: '' });

  // Search / sort / display
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [collapsedCats, setCollapsedCats] = useState({});
  const [collapsedDocs, setCollapsedDocs] = useState({});

  // Categories
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [catDialog, setCatDialog] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editCat, setEditCat] = useState(null); // { original, value }
  const isAdmin = authorized; // already checked ALLOWED_ROLES

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true);
    const [userSnap, settingsSnap] = await Promise.all([
      getDoc(doc(db, 'users', currentUser.uid)),
      getDoc(doc(db, 'settings', 'documentLibrary')),
    ]);
    const role = userSnap.data()?.role || '';
    if (!ALLOWED_ROLES.includes(role)) { setLoading(false); return; }
    setAuthorized(true);
    if (settingsSnap.exists() && settingsSnap.data().categories?.length) {
      setCategories(settingsSnap.data().categories);
    }
    await loadDocs();
    setLoading(false);
  }

  async function saveCategories(cats) {
    setCategories(cats);
    await setDoc(doc(db, 'settings', 'documentLibrary'), { categories: cats }, { merge: true });
  }

  async function loadDocs() {
    const snap = await getDocs(collection(db, 'documentLibrary'));
    const items = await Promise.all(snap.docs.map(async d => {
      const versionsSnap = await getDocs(
        query(collection(db, 'documentLibrary', d.id, 'versions'), orderBy('version', 'desc'))
      );
      const versions = versionsSnap.docs.map(v => ({ id: v.id, ...v.data() }));
      return { id: d.id, ...d.data(), versions };
    }));
    items.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
    setDocs(items);
  }

  async function handleCreateDoc() {
    if (!newDocForm.name.trim() || !newDocFile) return;
    const key = 'new_' + newDocFile.name;
    setUploadProgress(p => ({ ...p, [key]: 1 }));
    try {
      const storageRef = ref(storage,
        `documentLibrary/${Date.now()}_${newDocFile.name}`);
      const task = uploadBytesResumable(storageRef, newDocFile);
      const fileUrl = await new Promise((resolve, reject) => {
        task.on('state_changed',
          snap => setUploadProgress(p => ({
            ...p, [key]: Math.round(snap.bytesTransferred / snap.totalBytes * 100)
          })),
          reject,
          async () => resolve(await getDownloadURL(task.snapshot.ref))
        );
      });

      const docRef = await addDoc(collection(db, 'documentLibrary'), {
        name: newDocForm.name.trim(),
        category: newDocForm.category,
        expiresAt: newDocForm.expiresAt || null,
        currentVersion: 1,
        currentFileUrl: fileUrl,
        currentFileName: newDocFile.name,
        createdBy: currentUser.uid,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      await addDoc(collection(db, 'documentLibrary', docRef.id, 'versions'), {
        version: 1,
        fileUrl,
        fileName: newDocFile.name,
        note: newDocForm.note.trim() || 'Initial version',
        uploadedBy: currentUser.uid,
        uploadedAt: new Date()
      });

      setNewDocDialog(false);
      setNewDocForm({ name: '', category: 'Certifieringar', expiresAt: '', note: '' });
      setNewDocFile(null);
      setSaved('Dokument tillagt!');
      setTimeout(() => setSaved(''), 2000);
      loadDocs();
    } finally {
      setUploadProgress(p => { const n = { ...p }; delete n[key]; return n; });
    }
  }

  async function handleNewVersion(docId) {
    if (!newVersionFile) return;
    const docData = docs.find(d => d.id === docId);
    if (!docData) return;
    const newVer = (docData.currentVersion || 1) + 1;
    const key = 'ver_' + docId;
    setUploadProgress(p => ({ ...p, [key]: 1 }));
    try {
      const storageRef = ref(storage,
        `documentLibrary/${Date.now()}_${newVersionFile.name}`);
      const task = uploadBytesResumable(storageRef, newVersionFile);
      const fileUrl = await new Promise((resolve, reject) => {
        task.on('state_changed',
          snap => setUploadProgress(p => ({
            ...p, [key]: Math.round(snap.bytesTransferred / snap.totalBytes * 100)
          })),
          reject,
          async () => resolve(await getDownloadURL(task.snapshot.ref))
        );
      });

      await addDoc(collection(db, 'documentLibrary', docId, 'versions'), {
        version: newVer,
        fileUrl,
        fileName: newVersionFile.name,
        note: newVersionNote.trim() || `Version ${newVer}`,
        uploadedBy: currentUser.uid,
        uploadedAt: new Date()
      });

      await updateDoc(doc(db, 'documentLibrary', docId), {
        currentVersion: newVer,
        currentFileUrl: fileUrl,
        currentFileName: newVersionFile.name,
        updatedAt: new Date()
      });

      setNewVersionDialog(null);
      setNewVersionFile(null);
      setNewVersionNote('');
      setSaved(`Version ${newVer} uppladdad!`);
      setTimeout(() => setSaved(''), 2000);
      loadDocs();
    } finally {
      setUploadProgress(p => { const n = { ...p }; delete n[key]; return n; });
    }
  }

  async function handleUpdateExpiry(docId, expiresAt) {
    await updateDoc(doc(db, 'documentLibrary', docId), { expiresAt: expiresAt || null });
    setDocs(prev => prev.map(d => d.id === docId ? { ...d, expiresAt } : d));
  }

  function openEditMeta(d) {
    setEditMetaForm({
      name: d.name || '',
      category: d.category || 'Övrigt',
      expiresAt: d.expiresAt || '',
      note: d.note || ''
    });
    setEditMetaDoc(d);
  }

  async function handleSaveMeta() {
    if (!editMetaDoc || !editMetaForm.name.trim()) return;
    const updates = {
      name: editMetaForm.name.trim(),
      category: editMetaForm.category,
      expiresAt: editMetaForm.expiresAt || null,
      note: editMetaForm.note.trim(),
      updatedAt: new Date()
    };
    await updateDoc(doc(db, 'documentLibrary', editMetaDoc.id), updates);
    setDocs(prev => prev.map(d => d.id === editMetaDoc.id ? { ...d, ...updates } : d));
    setEditMetaDoc(null);
    setSaved('Metadata sparad!');
    setTimeout(() => setSaved(''), 2000);
    loadDocs();
  }

  async function handleDelete(docId) {
    const docData = docs.find(d => d.id === docId);
    // Ta bort alla versioner i Storage
    for (const v of (docData?.versions || [])) {
      try {
        const path = decodeURIComponent(v.fileUrl.split('/o/')[1].split('?')[0]);
        await deleteObject(ref(storage, path));
      } catch (_) {}
    }
    // Ta bort versions-subkollektion
    const batch = writeBatch(db);
    (docData?.versions || []).forEach(v => {
      batch.delete(doc(db, 'documentLibrary', docId, 'versions', v.id));
    });
    batch.delete(doc(db, 'documentLibrary', docId));
    await batch.commit();
    setDeleteTarget(null);
    loadDocs();
  }

  const filtered = docs.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      d.name?.toLowerCase().includes(q) ||
      d.category?.toLowerCase().includes(q) ||
      d.note?.toLowerCase().includes(q) ||
      d.currentFileName?.toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    if (sortBy === 'expiry') {
      va = a.expiresAt || '9999';
      vb = b.expiresAt || '9999';
    } else if (sortBy === 'category') {
      va = (a.category || '') + a.name;
      vb = (b.category || '') + b.name;
    } else {
      va = a.name || '';
      vb = b.name || '';
    }
    return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  });

  const grouped = categories.reduce((acc, cat) => {
    acc[cat] = sorted.filter(d => d.category === cat);
    return acc;
  }, {});

  function collapseAllDocs() {
    const all = {};
    docs.forEach(d => { all[d.id] = true; });
    setCollapsedDocs(all);
  }
  function expandAllDocs() { setCollapsedDocs({}); }
  function focusDoc(id) {
    const all = {};
    docs.forEach(d => { all[d.id] = d.id !== id; });
    setCollapsedDocs(all);
  }
  function collapseAllCats() {
    const all = {};
    categories.forEach(c => { all[c] = true; });
    setCollapsedCats(all);
  }
  function expandAllCats() { setCollapsedCats({}); }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );
  if (!authorized) return (
    <Box sx={{ p: 4 }}>
      <Alert severity="error">Du har inte behörighet att se denna sida.</Alert>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LagbasLogo />
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
            <IconButton color="inherit" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon />
              <Typography variant="h5" fontWeight="bold">Dokumentbibliotek</Typography>
            </Box>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: 'white', color: '#1a1a1a' }}
            onClick={() => setNewDocDialog(true)}>
            Nytt dokument
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3, pb: 6 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {docs.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <FolderIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Biblioteket är tomt
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => setNewDocDialog(true)}>
              Lägg till första dokumentet
            </Button>
          </Paper>
        ) : (
          <>
            {/* ── Toolbar ── */}
            <Paper sx={{ p: 1.5, mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Sök dokument..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
                sx={{ width: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Sortera</InputLabel>
                <Select value={sortBy} label="Sortera" onChange={e => setSortBy(e.target.value)}>
                  <MenuItem value="name">Namn</MenuItem>
                  <MenuItem value="category">Kategori</MenuItem>
                  <MenuItem value="expiry">Utgångsdatum</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title={sortDir === 'asc' ? 'Stigande' : 'Fallande'}>
                <IconButton size="small" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}>
                  {sortDir === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Chip
                label="Gruppera per kategori"
                size="small"
                onClick={() => setGroupByCategory(g => !g)}
                variant={groupByCategory ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer', bgcolor: groupByCategory ? '#FF6B00' : undefined, color: groupByCategory ? 'white' : undefined }}
              />
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Minimera alla">
                <IconButton size="small" onClick={() => { collapseAllDocs(); collapseAllCats(); }}>
                  <UnfoldLessIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Expandera alla">
                <IconButton size="small" onClick={() => { expandAllDocs(); expandAllCats(); }}>
                  <UnfoldMoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {sorted.length}/{docs.length}
              </Typography>
              {isAdmin && (
                <Tooltip title="Hantera kategorier">
                  <IconButton size="small" onClick={() => setCatDialog(true)}>
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Paper>

            {sorted.length === 0 && (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">Inga dokument matchar sökningen.</Typography>
              </Paper>
            )}

            {groupByCategory ? (
              categories.map(cat => {
                const catDocs = grouped[cat];
                if (!catDocs.length) return null;
                const catCollapsed = !!collapsedCats[cat];
                return (
                  <Box key={cat} sx={{ mb: 3 }}>
                    <Box
                      onClick={() => setCollapsedCats(prev => ({ ...prev, [cat]: !prev[cat] }))}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                        userSelect: 'none', mb: 1, '&:hover .cat-label': { color: '#FF6B00' } }}>
                      <Typography variant="overline" fontWeight="bold" className="cat-label"
                        sx={{ color: 'text.secondary', letterSpacing: 2, transition: 'color .15s' }}>
                        {cat} ({catDocs.length})
                      </Typography>
                      {catCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> : <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Collapse in={!catCollapsed}>
                      {catDocs.map(d => (
                        <DocRow
                          key={d.id}
                          doc={d}
                          collapsed={!!collapsedDocs[d.id]}
                          onToggleCollapse={() => setCollapsedDocs(prev => ({ ...prev, [d.id]: !prev[d.id] }))}
                          onFocus={() => focusDoc(d.id)}
                          expandedHistory={expandedHistory}
                          setExpandedHistory={setExpandedHistory}
                          uploadProgress={uploadProgress}
                          onNewVersion={() => setNewVersionDialog(d.id)}
                          onUpdateExpiry={val => handleUpdateExpiry(d.id, val)}
                          onEdit={() => openEditMeta(d)}
                          onDelete={() => setDeleteTarget(d)}
                        />
                      ))}
                    </Collapse>
                  </Box>
                );
              })
            ) : (
              sorted.map(d => (
                <DocRow
                  key={d.id}
                  doc={d}
                  collapsed={!!collapsedDocs[d.id]}
                  onToggleCollapse={() => setCollapsedDocs(prev => ({ ...prev, [d.id]: !prev[d.id] }))}
                  onFocus={() => focusDoc(d.id)}
                  expandedHistory={expandedHistory}
                  setExpandedHistory={setExpandedHistory}
                  uploadProgress={uploadProgress}
                  onNewVersion={() => setNewVersionDialog(d.id)}
                  onUpdateExpiry={val => handleUpdateExpiry(d.id, val)}
                  onEdit={() => openEditMeta(d)}
                  onDelete={() => setDeleteTarget(d)}
                />
              ))
            )}
          </>
        )}
      </Container>

      {/* Dialog: Nytt dokument */}
      <Dialog open={newDocDialog} onClose={() => setNewDocDialog(false)}
        maxWidth="sm" fullWidth>
        <DialogTitle>Lägg till dokument i biblioteket</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Namn" fullWidth value={newDocForm.name}
              onChange={e => setNewDocForm(f => ({ ...f, name: e.target.value }))}
              placeholder="t.ex. ISO 9001 certifikat 2025" />
            <FormControl fullWidth>
              <InputLabel>Kategori</InputLabel>
              <Select label="Kategori" value={newDocForm.category}
                onChange={e => setNewDocForm(f => ({ ...f, category: e.target.value }))}>
                {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Utgångsdatum (valfritt)" type="date" fullWidth
              value={newDocForm.expiresAt}
              onChange={e => setNewDocForm(f => ({ ...f, expiresAt: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
            <TextField label="Versionskommentar" fullWidth value={newDocForm.note}
              onChange={e => setNewDocForm(f => ({ ...f, note: e.target.value }))}
              placeholder="t.ex. Initial uppladdning" />
            <Button variant="outlined" component="label"
              startIcon={<UploadFileIcon />}
              sx={{ justifyContent: 'flex-start' }}>
              {newDocFile ? newDocFile.name : 'Välj fil (PDF, Word, bild…)'}
              <input type="file" hidden
                accept=".pdf,.docx,.doc,.png,.jpg,.jpeg"
                onChange={e => setNewDocFile(e.target.files[0] || null)} />
            </Button>
            {uploadProgress['new_' + newDocFile?.name] > 0 && (
              <LinearProgress variant="determinate"
                value={uploadProgress['new_' + newDocFile?.name]} />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDocDialog(false)}>Avbryt</Button>
          <Button variant="contained"
            disabled={!newDocForm.name.trim() || !newDocFile}
            onClick={handleCreateDoc}>
            Lägg till
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Ny version */}
      <Dialog open={!!newVersionDialog} onClose={() => setNewVersionDialog(null)}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          Ladda upp ny version —{' '}
          {docs.find(d => d.id === newVersionDialog)?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info" icon={false}>
              Nuvarande version:{' '}
              <strong>v{docs.find(d => d.id === newVersionDialog)?.currentVersion}</strong>
              {' '}→ ny version blir{' '}
              <strong>v{(docs.find(d => d.id === newVersionDialog)?.currentVersion || 0) + 1}</strong>
            </Alert>
            <Button variant="outlined" component="label"
              startIcon={<UploadFileIcon />}
              sx={{ justifyContent: 'flex-start' }}>
              {newVersionFile ? newVersionFile.name : 'Välj fil'}
              <input type="file" hidden
                accept=".pdf,.docx,.doc,.png,.jpg,.jpeg"
                onChange={e => setNewVersionFile(e.target.files[0] || null)} />
            </Button>
            <TextField label="Versionskommentar" fullWidth value={newVersionNote}
              onChange={e => setNewVersionNote(e.target.value)}
              placeholder="t.ex. Förnyad certifiering 2025" />
            {uploadProgress['ver_' + newVersionDialog] > 0 && (
              <LinearProgress variant="determinate"
                value={uploadProgress['ver_' + newVersionDialog]} />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewVersionDialog(null)}>Avbryt</Button>
          <Button variant="contained" disabled={!newVersionFile}
            onClick={() => handleNewVersion(newVersionDialog)}>
            Ladda upp
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Redigera metadata */}
      <Dialog open={!!editMetaDoc} onClose={() => setEditMetaDoc(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Redigera dokument – {editMetaDoc?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Namn" fullWidth value={editMetaForm.name}
              onChange={e => setEditMetaForm(f => ({ ...f, name: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Kategori</InputLabel>
              <Select label="Kategori" value={editMetaForm.category}
                onChange={e => setEditMetaForm(f => ({ ...f, category: e.target.value }))}>
                {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Utgångsdatum" type="date" fullWidth
              value={editMetaForm.expiresAt}
              onChange={e => setEditMetaForm(f => ({ ...f, expiresAt: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
            <TextField label="Kommentar" fullWidth multiline rows={2}
              value={editMetaForm.note}
              onChange={e => setEditMetaForm(f => ({ ...f, note: e.target.value }))}
              placeholder="t.ex. Gäller alla projekt" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMetaDoc(null)}>Avbryt</Button>
          <Button variant="contained" disabled={!editMetaForm.name.trim()}
            onClick={handleSaveMeta}>
            Spara
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Hantera kategorier */}
      <Dialog open={catDialog} onClose={() => { setCatDialog(false); setNewCatName(''); setEditCat(null); }}
        maxWidth="xs" fullWidth>
        <DialogTitle>Hantera kategorier</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {categories.map((cat, i) => (
              <Box key={cat} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {editCat?.original === cat ? (
                  <TextField size="small" fullWidth autoFocus value={editCat.value}
                    onChange={e => setEditCat(ec => ({ ...ec, value: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && editCat.value.trim()) {
                        const updated = categories.map(c => c === cat ? editCat.value.trim() : c);
                        saveCategories(updated);
                        setEditCat(null);
                      }
                      if (e.key === 'Escape') setEditCat(null);
                    }} />
                ) : (
                  <Typography variant="body2" sx={{ flex: 1, py: 0.5 }}>{cat}</Typography>
                )}
                <IconButton size="small" onClick={() => setEditCat(editCat?.original === cat ? null : { original: cat, value: cat })}>
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton size="small" onClick={() => saveCategories(categories.filter((_, j) => j !== i))}
                  sx={{ color: 'error.main', opacity: 0.6 }}>
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" fullWidth placeholder="Ny kategori..."
                value={newCatName} onChange={e => setNewCatName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newCatName.trim() && !categories.includes(newCatName.trim())) {
                    saveCategories([...categories, newCatName.trim()]);
                    setNewCatName('');
                  }
                }} />
              <Button size="small" variant="contained" disabled={!newCatName.trim() || categories.includes(newCatName.trim())}
                onClick={() => { saveCategories([...categories, newCatName.trim()]); setNewCatName(''); }}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, whiteSpace: 'nowrap' }}>
                Lägg till
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCatDialog(false); setNewCatName(''); setEditCat(null); }}>Stäng</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Ta bort */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Ta bort dokument</DialogTitle>
        <DialogContent>
          <Typography>
            Ta bort <strong>{deleteTarget?.name}</strong> och alla{' '}
            {deleteTarget?.versions?.length} version(er)?
            Detta kan inte ångras.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Avbryt</Button>
          <Button color="error" variant="contained"
            onClick={() => handleDelete(deleteTarget.id)}>
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function DocRow({ doc, collapsed, onToggleCollapse, onFocus,
  expandedHistory, setExpandedHistory, uploadProgress,
  onNewVersion, onUpdateExpiry, onEdit, onDelete }) {
  const [editExpiry, setEditExpiry] = useState(false);
  const [expiryVal, setExpiryVal] = useState(doc.expiresAt || '');
  const historyOpen = expandedHistory[doc.id];

  return (
    <Paper sx={{ mb: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      {/* Header row – always visible */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2,
        cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
        borderBottom: collapsed ? 'none' : '1px solid', borderColor: 'divider' }}>
        <InsertDriveFileIcon sx={{ color: '#FF6B00', fontSize: 20, flexShrink: 0 }}
          onClick={onToggleCollapse} />
        <Box sx={{ flex: 1, minWidth: 0 }} onClick={onToggleCollapse}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography fontWeight="bold" component="a" href={doc.currentFileUrl}
              target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              sx={{ color: 'text.primary', textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' } }}>
              {doc.name}
            </Typography>
            <Chip label={`v${doc.currentVersion}`} size="small"
              sx={{ bgcolor: '#1a1a1a', color: 'white', fontSize: 11 }} />
            <ExpiryChip expiresAt={doc.expiresAt} />
            {!collapsed && doc.category && (
              <Chip label={doc.category} size="small" variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }} />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {!collapsed && (
            <Tooltip title="Fokusera">
              <Button size="small" variant="text"
                sx={{ fontSize: '0.65rem', color: 'text.secondary', minWidth: 0, px: 0.8 }}
                onClick={e => { e.stopPropagation(); onFocus(); }}>
                Fokus
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Redigera metadata">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onEdit(); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ladda upp ny version">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onNewVersion(); }}>
              <UploadFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Versionshistorik">
            <IconButton size="small" onClick={e => {
              e.stopPropagation();
              setExpandedHistory(prev => ({ ...prev, [doc.id]: !prev[doc.id] }));
            }}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ta bort">
            <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onDelete(); }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onToggleCollapse}>
            {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {/* Expandable details */}
      <Collapse in={!collapsed}>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {doc.currentFileName}
            {doc.note && <> · <span style={{ fontStyle: 'italic' }}>{doc.note}</span></>}
          </Typography>

          <Box sx={{ mt: 0.5 }}>
            {editExpiry ? (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                <TextField type="date" size="small" value={expiryVal}
                  onChange={e => setExpiryVal(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  label="Utgångsdatum" />
                <Button size="small" variant="contained"
                  onClick={() => { onUpdateExpiry(expiryVal); setEditExpiry(false); }}>
                  Spara
                </Button>
                <Button size="small" onClick={() => setEditExpiry(false)}>Avbryt</Button>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => setEditExpiry(true)}>
                {doc.expiresAt ? `Utgår ${doc.expiresAt}` : '+ Sätt utgångsdatum'}
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>

      {/* Progress + history always visible when open */}
      <Box sx={{ px: 2 }}>
      {uploadProgress['ver_' + doc.id] > 0 && (
        <LinearProgress variant="determinate"
          value={uploadProgress['ver_' + doc.id]} sx={{ mt: 1 }} />
      )}

      {uploadProgress['ver_' + doc.id] > 0 && (
        <LinearProgress variant="determinate"
          value={uploadProgress['ver_' + doc.id]} sx={{ mt: 1 }} />
      )}

      {/* Versionshistorik */}
      <Collapse in={historyOpen}>
        <Box sx={{ mt: 1.5, pl: 4, borderLeft: '2px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <HistoryIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              VERSIONSHISTORIK
            </Typography>
          </Box>
          <List dense disablePadding>
            {doc.versions?.map((v, i) => (
              <ListItem key={v.id} disablePadding sx={{ py: 0.3 }}>
                <Chip label={`v${v.version}`} size="small"
                  sx={{
                    mr: 1, fontSize: 10, height: 18,
                    bgcolor: i === 0 ? '#1a1a1a' : 'default',
                    color: i === 0 ? 'white' : 'text.primary'
                  }} />
                <ListItemText
                  primary={
                    <Typography variant="caption" component="a" href={v.fileUrl}
                      target="_blank" rel="noopener noreferrer"
                      sx={{ color: 'text.primary', textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' } }}>
                      {v.fileName}
                      {i === 0 && (
                        <Chip label="Aktuell" size="small"
                          sx={{ ml: 0.5, fontSize: 9, height: 16,
                            bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                      )}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {v.note}
                      {v.uploadedAt && ` · ${new Date(
                        v.uploadedAt.toDate?.() || v.uploadedAt
                      ).toLocaleDateString('sv-SE')}`}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Collapse>
      </Box>
    </Paper>
  );
}
